/**
 * The buyer's face: what I have asked for, and asking for something new.
 *
 * The form validates with `requirementSchema` from `@bjsfabric/shared` — the
 * same object the server validates with. That is the whole reason the backend
 * is TypeScript: a requirement this form accepts is a requirement the API
 * accepts, because there is only one definition of what a requirement is.
 */
import { requirementSchema } from '@bjsfabric/shared'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { api } from '../api/client'
import {
  AttachmentsPanel,
  Button,
  CatalogFabricPanel,
  DeliveryProgress,
  Empty,
  Field,
  Loading,
  Pill,
  Problem,
  leadTime,
  measurement,
  shortDate,
  useRefreshOnFocus,
  type CatalogFabricView,
} from '../ui'

export interface RequirementView {
  id: string
  reference: string
  status: string
  fabricDescription: string
  catalogFabric?: CatalogFabricView
  widthValue: string | null
  widthUnit: string | null
  gsm: number | null
  shrinkageLength: string | null
  shrinkageWidth: string | null
  spirality: string | null
  repeatRequired: boolean | null
  quantity: string | null
  quantityUnit: string | null
  requiredBy: string | null
  notes: string | null
  buyerName?: string
  submittedAt: string | null
  // Present only for the buyer and the admin — see `RequirementsService.present`.
  // `chosenReplyId` names which mill's reply this was built from, and only the
  // admin ever gets it: the buyer is shown a price, never who it came from.
  rejectedAt?: string | null
  rejectionNote?: string | null
  quotedPrice?: string | null
  quotedCurrency?: string | null
  quotedLeadTimeDays?: number | null
  quotedNote?: string | null
  quotedAt?: string | null
  confirmedAt?: string | null
  chosenReplyId?: string | null
}

type StatusFilter = 'ALL' | 'DRAFT' | 'SENT' | 'REJECTED' | 'QUOTED' | 'IN_PROGRESS' | 'CLOSED'

/** Buyer-facing wording for a status that means something more specific to them than the raw enum. */
function buyerStatusLabel(status: string): string | undefined {
  // ENQUIRED is the admin's word for it. From here it just looks like someone
  // finally looked at what was sent.
  if (status === 'ENQUIRED') return 'Viewed'
  return undefined
}

export function RequirementCard({
  requirement,
  href,
  statusLabel,
}: {
  requirement: RequirementView
  href?: string
  statusLabel?: string
}) {
  const body = (
    <>
      <div className="card-head">
        <strong>{requirement.fabricDescription}</strong>
        <Pill value={requirement.status} label={statusLabel} />
      </div>
      <div className="reference">
        {requirement.reference}
        {requirement.buyerName ? ` · ${requirement.buyerName}` : ''}
      </div>
      <ul className="specs">
        <li>
          GSM <b>{measurement(requirement.gsm)}</b>
        </li>
        <li>
          Width <b>{measurement(requirement.widthValue, requirement.widthUnit)}</b>
        </li>
        <li>
          Shrink L/W{' '}
          <b>
            {measurement(requirement.shrinkageLength)} / {measurement(requirement.shrinkageWidth)}
          </b>
        </li>
        <li>
          Spirality <b>{measurement(requirement.spirality)}</b>
        </li>
        <li>
          Repeat{' '}
          <b>
            {requirement.repeatRequired === null || requirement.repeatRequired === undefined
              ? '—'
              : requirement.repeatRequired
                ? 'yes'
                : 'no'}
          </b>
        </li>
        <li>
          Quantity <b>{measurement(requirement.quantity, requirement.quantityUnit)}</b>
        </li>
        <li>
          Wanted by <b>{shortDate(requirement.requiredBy)}</b>
        </li>
      </ul>
      {requirement.notes ? <p className="field-hint">{requirement.notes}</p> : null}
    </>
  )

  return href ? (
    <Link className="card card-link" to={href}>
      {body}
    </Link>
  ) : (
    <div className="card">{body}</div>
  )
}

export function BuyerRequirements() {
  const [requirements, setRequirements] = useState<RequirementView[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [filter, setFilter] = useState<StatusFilter>('ALL')
  const [drawerOpen, setDrawerOpen] = useState(false)

  const load = async () => {
    try {
      const result = await api.get<{ requirements: RequirementView[] }>('/requirements')
      setRequirements(result.requirements)
    } catch (problem) {
      setError(problem)
    }
  }

  useEffect(() => {
    void load()
  }, [])
  // A requirement can be quoted, or an order confirmed, entirely by someone
  // else's action — catch it when the buyer comes back to a tab left open.
  useRefreshOnFocus(() => void load())

  if (!requirements && !error) return <Loading />

  // "Sent" folds SUBMITTED and ENQUIRED together: to a buyer both just mean
  // "the administrator has it and hasn't priced it yet" — the distinction
  // between them is the admin's own workflow, not the buyer's.
  const sections: { key: StatusFilter; label: string; items: RequirementView[] }[] = [
    { key: 'DRAFT', label: 'Draft', items: requirements?.filter((r) => r.status === 'DRAFT') ?? [] },
    {
      key: 'SENT',
      label: 'Sent',
      items: requirements?.filter((r) => r.status === 'SUBMITTED' || r.status === 'ENQUIRED') ?? [],
    },
    {
      key: 'REJECTED',
      label: 'Rejected',
      items: requirements?.filter((r) => r.status === 'REJECTED') ?? [],
    },
    {
      key: 'QUOTED',
      label: 'Price ready',
      items: requirements?.filter((r) => r.status === 'QUOTED') ?? [],
    },
    {
      key: 'IN_PROGRESS',
      label: 'In progress',
      items: requirements?.filter((r) => r.status === 'IN_PROGRESS') ?? [],
    },
    { key: 'CLOSED', label: 'Closed', items: requirements?.filter((r) => r.status === 'CLOSED') ?? [] },
  ]
  const shows = (key: StatusFilter) => filter === 'ALL' || filter === key
  const activeLabel = sections.find((section) => section.key === filter)?.label

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h1>Your requirements</h1>
        <Button variant="quiet" onClick={() => setDrawerOpen(true)}>
          {activeLabel ?? 'All'} ▾
        </Button>
      </div>
      <p className="lede">A draft is yours alone until you send it.</p>
      <Problem error={error} />

      {drawerOpen ? (
        <>
          <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />
          <nav className="drawer">
            <div className="drawer-head">
              <strong>Show</strong>
              <Button variant="quiet" onClick={() => setDrawerOpen(false)}>
                Close
              </Button>
            </div>
            <button
              className={`drawer-item${filter === 'ALL' ? ' drawer-item-active' : ''}`}
              onClick={() => {
                setFilter('ALL')
                setDrawerOpen(false)
              }}
            >
              <span>All</span>
              <span className="drawer-count">{requirements?.length ?? 0}</span>
            </button>
            {sections.map((section) => (
              <button
                key={section.key}
                className={`drawer-item${filter === section.key ? ' drawer-item-active' : ''}`}
                onClick={() => {
                  setFilter(section.key)
                  setDrawerOpen(false)
                }}
              >
                <span>{section.label}</span>
                <span className="drawer-count">{section.items.length}</span>
              </button>
            ))}
          </nav>
        </>
      ) : null}

      <div className="actions" style={{ marginBottom: 16 }}>
        <Link className="button button-primary" to="/new" style={{ lineHeight: '44px' }}>
          New requirement
        </Link>
        <Link className="button button-quiet" to="/fabrics" style={{ lineHeight: '44px' }}>
          Browse fabric catalog
        </Link>
      </div>

      {requirements?.length === 0 ? <Empty>Nothing yet.</Empty> : null}

      {sections.map((section) =>
        shows(section.key)
          ? section.items.map((requirement) => (
              <RequirementCard
                key={requirement.id}
                requirement={requirement}
                href={`/requirements/${requirement.id}`}
                statusLabel={buyerStatusLabel(requirement.status)}
              />
            ))
          : null,
      )}

      {filter !== 'ALL' && sections.find((section) => section.key === filter)?.items.length === 0 ? (
        <Empty>Nothing here.</Empty>
      ) : null}
    </>
  )
}

const BLANK = {
  fabricDescription: '',
  widthValue: '',
  widthUnit: 'INCH',
  gsm: '',
  shrinkageLength: '',
  shrinkageWidth: '',
  spirality: '',
  repeatRequired: '',
  quantity: '',
  quantityUnit: 'kg',
  requiredBy: '',
  notes: '',
}

type FormState = typeof BLANK

/** '' means "not stated", which is null — not zero. */
function stated(value: string): string | null {
  return value.trim() === '' ? null : value.trim()
}

/** The reverse of `buildCandidate`: a stored requirement, back into editable strings. */
function formFromRequirement(requirement: RequirementView): FormState {
  return {
    fabricDescription: requirement.fabricDescription,
    widthValue: requirement.widthValue ?? '',
    widthUnit: requirement.widthUnit ?? 'INCH',
    gsm: requirement.gsm != null ? String(requirement.gsm) : '',
    shrinkageLength: requirement.shrinkageLength ?? '',
    shrinkageWidth: requirement.shrinkageWidth ?? '',
    spirality: requirement.spirality ?? '',
    repeatRequired:
      requirement.repeatRequired === null || requirement.repeatRequired === undefined
        ? ''
        : requirement.repeatRequired
          ? 'yes'
          : 'no',
    quantity: requirement.quantity ?? '',
    quantityUnit: requirement.quantityUnit ?? 'kg',
    requiredBy: requirement.requiredBy ?? '',
    notes: requirement.notes ?? '',
  }
}

/** Form strings into the shape `requirementSchema` validates — shared by creating and editing. */
function buildCandidate(form: FormState) {
  const widthValue = stated(form.widthValue)
  return {
    fabricDescription: form.fabricDescription.trim(),
    // The unit only travels when there is a value to attach it to; sending a
    // unit alone is the half-set width the schema exists to refuse.
    widthValue,
    widthUnit: widthValue ? form.widthUnit : null,
    gsm: stated(form.gsm),
    shrinkageLength: stated(form.shrinkageLength),
    shrinkageWidth: stated(form.shrinkageWidth),
    spirality: stated(form.spirality),
    repeatRequired: form.repeatRequired === '' ? null : form.repeatRequired === 'yes',
    quantity: stated(form.quantity),
    quantityUnit: stated(form.quantity) ? form.quantityUnit : null,
    requiredBy: stated(form.requiredBy),
    notes: stated(form.notes),
  }
}

/** Turn `requirementSchema`'s issues into one message per field, first one wins. */
function issuesFrom(error: z.ZodError): Record<string, string> {
  const found: Record<string, string> = {}
  for (const issue of error.issues) {
    const path = issue.path.join('.')
    if (!found[path]) found[path] = issue.message
  }
  return found
}

/** Every field on the sheet. No `<form>` wrapper and no buttons — those differ between creating and editing. */
function RequirementFormFields({
  form,
  set,
  issues,
}: {
  form: FormState
  set: (key: keyof FormState) => (event: { target: { value: string } }) => void
  issues: Record<string, string>
}) {
  return (
    <>
      <Field
        label="What fabric"
        hint="How you would say it on the phone, e.g. 30s combed cotton single jersey"
        error={issues['fabricDescription']}
      >
        <input required value={form.fabricDescription} onChange={set('fabricDescription')} />
      </Field>

      <div className="row">
        <Field label="Width" error={issues['widthValue'] ?? issues['widthUnit']}>
          <input
            inputMode="decimal"
            value={form.widthValue}
            onChange={set('widthValue')}
            placeholder="68"
          />
        </Field>
        <Field label="Unit" hint="Tubular in inches, open width in cm">
          <select value={form.widthUnit} onChange={set('widthUnit')}>
            <option value="INCH">inches</option>
            <option value="CM">cm</option>
          </select>
        </Field>
      </div>

      <Field label="GSM" error={issues['gsm']}>
        <input inputMode="numeric" value={form.gsm} onChange={set('gsm')} placeholder="180" />
      </Field>

      <div className="row">
        <Field
          label="Shrinkage length %"
          hint="Negative for shrinkage, positive for growth"
          error={issues['shrinkageLength']}
        >
          <input
            inputMode="text"
            value={form.shrinkageLength}
            onChange={set('shrinkageLength')}
            placeholder="-4.5"
          />
        </Field>
        <Field
          label="Shrinkage width %"
          hint="Negative for shrinkage, positive for growth"
          error={issues['shrinkageWidth']}
        >
          <input
            inputMode="text"
            value={form.shrinkageWidth}
            onChange={set('shrinkageWidth')}
            placeholder="-3.0"
          />
        </Field>
      </div>

      <div className="row">
        <Field
          label="Spirality %"
          hint="Which way it skews — either sign is normal"
          error={issues['spirality']}
        >
          <input
            inputMode="text"
            value={form.spirality}
            onChange={set('spirality')}
            placeholder="2.5"
          />
        </Field>
        <Field label="Repeat required">
          <select value={form.repeatRequired} onChange={set('repeatRequired')}>
            <option value="">Not stated</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </Field>
      </div>

      <div className="row">
        <Field label="Quantity" error={issues['quantity']}>
          <input
            inputMode="decimal"
            value={form.quantity}
            onChange={set('quantity')}
            placeholder="5000"
          />
        </Field>
        <Field label="Unit">
          <select value={form.quantityUnit} onChange={set('quantityUnit')}>
            <option value="kg">kg</option>
            <option value="m">metres</option>
            <option value="pcs">pieces</option>
          </select>
        </Field>
      </div>

      <Field label="Wanted by" error={issues['requiredBy']}>
        <input type="date" value={form.requiredBy} onChange={set('requiredBy')} />
      </Field>

      <Field label="Anything else" error={issues['notes']}>
        <textarea value={form.notes} onChange={set('notes')} />
      </Field>
    </>
  )
}

/** Just what `NewRequirement` needs from `GET /fabrics/:id` to prefill the form. */
interface FabricDetail {
  id: string
  label: string
  fabricCode: string
  finishedGsm: number | null
  finishedWidth: string | null
  widthUnit: string | null
}

export function NewRequirement() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [form, setForm] = useState<FormState>({ ...BLANK })
  const [issues, setIssues] = useState<Record<string, string>>({})
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  // Set once, from the catalogue picker (`/fabrics`) -- not part of `FormState`
  // because nothing in the form edits it directly; picking a different fabric
  // means going back to `/fabrics`, not clearing a field.
  const [pickedFabric, setPickedFabric] = useState<FabricDetail | null>(null)

  useEffect(() => {
    const fabricId = searchParams.get('fabricId')
    if (!fabricId) return
    ;(async () => {
      try {
        const result = await api.get<{ fabric: FabricDetail }>(`/fabrics/${fabricId}`)
        setPickedFabric(result.fabric)
        setForm((current) => ({
          ...current,
          fabricDescription: result.fabric.label,
          widthValue: result.fabric.finishedWidth ?? '',
          widthUnit: result.fabric.widthUnit ?? current.widthUnit,
          gsm: result.fabric.finishedGsm != null ? String(result.fabric.finishedGsm) : '',
        }))
      } catch (problem) {
        setError(problem)
      }
      // Deliberately runs once, on the id this screen was opened with --
      // re-fetching on every `searchParams` identity change isn't needed
      // since nothing in this screen rewrites `?fabricId=` afterward.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })()
  }, [])

  const set = (key: keyof FormState) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setIssues({})

    // Checked here with the server's own schema, so the person gets the answer
    // without a round trip — and gets the identical answer either way.
    const parsed = requirementSchema.safeParse({
      ...buildCandidate(form),
      fabricMasterId: pickedFabric?.id ?? null,
    })
    if (!parsed.success) {
      setIssues(issuesFrom(parsed.error as z.ZodError))
      return
    }

    setBusy(true)
    try {
      const result = await api.post<{ requirement: RequirementView }>('/requirements', parsed.data)
      // To the review screen, not the list — "view and confirm" is the step
      // between writing a draft and sending it to the administrator.
      navigate(`/requirements/${result.requirement.id}`, { replace: true })
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h1>New requirement</h1>
      <p className="lede">
        Only the fabric is required. Send what you know — the rest can follow.
      </p>
      <Problem error={error} />

      {pickedFabric ? (
        <div className="card">
          <div className="card-head">
            <strong>From catalogue: {pickedFabric.fabricCode}</strong>
          </div>
          <p className="field-hint">{pickedFabric.label}</p>
          <div className="actions" style={{ marginTop: 0 }}>
            <Link className="button button-quiet" to="/fabrics">
              Change
            </Link>
          </div>
        </div>
      ) : null}

      <form onSubmit={submit}>
        <RequirementFormFields form={form} set={set} issues={issues} />
        <div className="actions">
          <Button type="submit" busy={busy}>
            Save as draft
          </Button>
          <Link className="button button-quiet" to="/" style={{ lineHeight: '44px' }}>
            Cancel
          </Link>
        </div>
      </form>
    </>
  )
}

/**
 * The buyer's own detail screen: a draft can be edited and sent from here;
 * anything past that is a read-only view of where it stands, plus whatever
 * action that stage calls for (confirming a quote).
 *
 * Editing is offered only for `DRAFT`, even though the server still allows a
 * `SUBMITTED` one to be patched too (nothing has looked at it yet) — this is
 * the specific gap that was reported: a draft with no way to change it at
 * all. Extending the button to `SUBMITTED` is a one-line change if that is
 * wanted next.
 */
export function RequirementDetail() {
  const { id = '' } = useParams()
  const [requirement, setRequirement] = useState<RequirementView | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<FormState>({ ...BLANK })
  const [issues, setIssues] = useState<Record<string, string>>({})

  const load = async () => {
    try {
      const result = await api.get<{ requirement: RequirementView }>(`/requirements/${id}`)
      setRequirement(result.requirement)
      setForm(formFromRequirement(result.requirement))
    } catch (problem) {
      setError(problem)
    }
  }

  useEffect(() => {
    void load()
  }, [id])
  // Skipped while `editing`: reloading over an open edit would silently
  // overwrite whatever the buyer has typed and not yet saved.
  useRefreshOnFocus(() => {
    if (!editing) void load()
  })

  if (!requirement && !error) return <Loading />

  const set = (key: keyof FormState) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const saveEdit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setIssues({})

    const parsed = requirementSchema.safeParse(buildCandidate(form))
    if (!parsed.success) {
      setIssues(issuesFrom(parsed.error as z.ZodError))
      return
    }

    setBusy(true)
    try {
      const result = await api.patch<{ requirement: RequirementView }>(
        `/requirements/${id}`,
        parsed.data,
      )
      setRequirement(result.requirement)
      setEditing(false)
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  const act = async (action: 'submit' | 'confirm') => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.post<{ requirement: RequirementView }>(
        `/requirements/${id}/${action}`,
      )
      setRequirement(result.requirement)
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Link to="/" className="reference">
        ← All requirements
      </Link>
      <h1 style={{ marginTop: 8 }}>{requirement?.fabricDescription}</h1>
      <Problem error={error} />

      {editing && requirement ? (
        <form onSubmit={saveEdit}>
          <RequirementFormFields form={form} set={set} issues={issues} />
          <div className="actions">
            <Button type="submit" busy={busy}>
              Save changes
            </Button>
            <Button
              type="button"
              variant="quiet"
              onClick={() => {
                setEditing(false)
                setForm(formFromRequirement(requirement))
                setIssues({})
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : requirement ? (
        <>
          <RequirementCard requirement={requirement} statusLabel={buyerStatusLabel(requirement.status)} />

          {requirement.catalogFabric ? <CatalogFabricPanel fabric={requirement.catalogFabric} /> : null}

          <AttachmentsPanel kind="requirement" id={requirement.id} canUpload />

          {requirement.status === 'REJECTED' ? (
            <div className="card">
              <div className="card-head">
                <strong>Not accepted</strong>
              </div>
              {requirement.rejectionNote ? (
                <p className="field-hint">{requirement.rejectionNote}</p>
              ) : (
                <p className="field-hint">No reason was given.</p>
              )}
              <div className="actions">
                <Link className="button button-primary" to="/new" style={{ lineHeight: '44px' }}>
                  New requirement
                </Link>
              </div>
            </div>
          ) : null}

          {requirement.status === 'QUOTED' ? (
            <div className="card">
              <div className="card-head">
                <strong>A price is ready</strong>
              </div>
              <ul className="specs">
                <li>
                  Price{' '}
                  <b>
                    {measurement(requirement.quotedPrice ?? null)} {requirement.quotedCurrency ?? ''}
                  </b>
                </li>
                <li>
                  Lead time <b>{leadTime(requirement.quotedLeadTimeDays ?? null)}</b>
                </li>
              </ul>
              {requirement.quotedNote ? (
                <p className="field-hint">{requirement.quotedNote}</p>
              ) : null}
              <div className="actions">
                <Button busy={busy} onClick={() => act('confirm')}>
                  Confirm order
                </Button>
              </div>
            </div>
          ) : null}

          {requirement.status === 'IN_PROGRESS' ? (
            <div className="card">
              <div className="card-head">
                <strong>On order</strong>
              </div>
              <ul className="specs">
                <li>
                  Price{' '}
                  <b>
                    {measurement(requirement.quotedPrice ?? null)} {requirement.quotedCurrency ?? ''}
                  </b>
                </li>
                <li>
                  Lead time <b>{leadTime(requirement.quotedLeadTimeDays ?? null)}</b>
                </li>
              </ul>
              <DeliveryProgress
                confirmedAt={requirement.confirmedAt}
                leadTimeDays={requirement.quotedLeadTimeDays}
              />
            </div>
          ) : null}

          <div className="actions" style={{ marginTop: 16 }}>
            {requirement.status === 'DRAFT' ? (
              <>
                <Button variant="quiet" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <Button busy={busy} onClick={() => act('submit')}>
                  Send to the administrator
                </Button>
              </>
            ) : null}
            <Link className="button button-quiet" to="/" style={{ lineHeight: '44px' }}>
              Back to list
            </Link>
          </div>
        </>
      ) : null}
    </>
  )
}
